import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Heading, IndicatorDot, Paragraph } from '@vwfs-bronson/bronson-react';
import { format } from 'date-fns';
import { assertDefined } from '../../services/common/miscUtils';
import { useRemoveCardTitle } from '../../services/common/useRemoveCardTitle';
import { notesSelectors, setCurrentConversation } from '../../services/redux/features/notes.slice';
import {
  getFirstParticipantOfType,
  getFirstTextMessage,
  getInitiatorParticipant,
  getReferenceData,
  toggleUnreadStatus,
  translateReferenceType,
} from './conversationUtils';

interface ConversationListItemProps {
  conversationItem: MilesPosNotesIntegrationService.Components.Schemas.Conversation;
  isActiveConversation: boolean;
}

export const ConversationListItem: FunctionComponent<ConversationListItemProps> = ({
  conversationItem,
  isActiveConversation,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  useRemoveCardTitle();
  const reference = getReferenceData(conversationItem);
  const hasUnreadMessages = (conversationItem?.unreadMessages ?? 0) > 0 || !conversationItem?.isSeenByCurrentUser;
  const conversations = useSelector(notesSelectors.selectConversations);

  const onToggleUnreadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    toggleUnreadStatus(dispatch, assertDefined(conversationItem.conversationId), hasUnreadMessages, conversations);
  };

  return (
    <Card
      title=""
      key={conversationItem.conversationId}
      className={`u-mb-small u-text-left${isActiveConversation ? ' u-bg-alternative' : ''}`}
      onClick={() => {
        if (!isActiveConversation) {
          dispatch(setCurrentConversation(conversationItem));
        }
        if (hasUnreadMessages) {
          toggleUnreadStatus(
            dispatch,
            assertDefined(conversationItem.conversationId),
            hasUnreadMessages,
            conversations
          );
        }
      }}
    >
      <Paragraph>{getFirstParticipantOfType(conversationItem, 'BACK_OFFICE')?.name}</Paragraph>

      {/* <Heading level={5} className="u-mb-small">
        {metadata.entityFullName}
      </Heading> */}

      <Paragraph>
        {reference?.objectType ? translateReferenceType(reference.objectType, t) : ''}&nbsp;
        {reference?.humanReadableObjectReferenceId}
      </Paragraph>

      <Heading level={6} className="u-mb-none">
        {hasUnreadMessages && <IndicatorDot label="" status="unread" />}
        <a href="" className="c-notification-center__item__heading-link" onClick={(event) => event.preventDefault()}>
          {conversationItem.title}
        </a>
      </Heading>

      <Paragraph>{getFirstTextMessage(conversationItem)?.message}</Paragraph>

      <Paragraph className="u-text-muted u-font-size-fs-1">
        {getInitiatorParticipant(conversationItem.participants)?.name}
      </Paragraph>

      <time className="u-text-muted u-font-size-fs-1" dateTime={getFirstTextMessage(conversationItem)?.timeStamp}>
        {getFirstTextMessage(conversationItem)?.timeStamp
          ? format(new Date(getFirstTextMessage(conversationItem)?.timeStamp || ''), 'dd.MM.yyyy, HH:mm')
          : ''}
      </time>

      <Paragraph>
        <Button
          small
          simple
          className="u-mt-small"
          secondary
          link
          icon="semantic-forward"
          onClick={onToggleUnreadClick}
        >
          {hasUnreadMessages ? t('conversationHeader:markAsRead') : t('conversationHeader:markAsUnread')}
        </Button>
      </Paragraph>
    </Card>
  );
};
